import { useState } from 'react';
import { Music, Sparkles, RefreshCw } from 'lucide-react';
import { MUSIC_MODELS } from '../lib/models';
import PageHeader from '../components/ui/PageHeader';
import GlassCard from '../components/ui/GlassCard';
import Button from '../components/ui/Button';
import Slider from '../components/ui/Slider';
import PromptInput from '../components/generation/PromptInput';
import ModelSelector from '../components/generation/ModelSelector';
import GenerationProgress from '../components/generation/GenerationProgress';
import ResultReveal from '../components/generation/ResultReveal';
import WaveformPlayer from '../components/generation/WaveformPlayer';
import { useGenerate } from '../hooks/useGenerate';

const SAMPLES = [
  'Lo-fi hip hop beat with mellow piano and vinyl crackle',
  'Epic orchestral build with soaring strings and timpani',
  'Dreamy synthwave with a driving bassline, 80s vibe',
];

export default function MusicStudio() {
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState(MUSIC_MODELS[0].value);
  const [duration, setDuration] = useState(10);
  const { generate, loading, elapsed, result } = useGenerate('/generate/music');

  const run = () => prompt.trim() && generate({ prompt, model, duration });

  return (
    <div>
      <PageHeader
        title="Music studio"
        subtitle="Describe a mood — MusicGen scores it."
        icon={Music}
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        <div className="space-y-4">
          <PromptInput
            value={prompt}
            onChange={setPrompt}
            onSubmit={run}
            loading={loading}
            placeholder="A warm lo-fi beat with rainy ambience…"
            actionLabel="Compose"
          />
          <GlassCard className="space-y-5 p-4">
            <ModelSelector value={model} onChange={setModel} models={MUSIC_MODELS} />
            <Slider
              label="Duration"
              value={duration}
              min={2}
              max={30}
              step={1}
              suffix="s"
              onChange={setDuration}
            />
          </GlassCard>

          <div className="flex flex-wrap gap-2">
            {SAMPLES.map((s) => (
              <button
                key={s}
                onClick={() => setPrompt(s)}
                className="rounded-full border border-border/70 px-3 py-1.5 text-xs text-content-muted transition-colors hover:border-accent-cyan hover:text-content"
              >
                {s.slice(0, 26)}…
              </button>
            ))}
          </div>
        </div>

        <GlassCard spotlight className="flex min-h-[420px] items-center justify-center p-6">
          {loading ? (
            <GenerationProgress elapsed={elapsed} label="Composing track" />
          ) : result ? (
            <ResultReveal className="w-full">
              <div className="mb-5 text-center">
                <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-cyan/20 to-brand-500/20">
                  <Music className="h-8 w-8 text-accent-cyan" />
                </div>
                <p className="line-clamp-2 px-4 text-sm text-content-muted">{result.prompt}</p>
              </div>
              <WaveformPlayer src={result.outputUrl} filename={`aether-${result.id}.flac`} />
              <div className="mt-4 flex justify-center">
                <Button variant="secondary" size="sm" icon={RefreshCw} onClick={run}>
                  Compose another
                </Button>
              </div>
            </ResultReveal>
          ) : (
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="rounded-2xl bg-accent-cyan/10 p-4 glow-pulse">
                <Sparkles className="h-8 w-8 text-accent-cyan" />
              </div>
              <p className="font-medium">Your track will appear here</p>
              <p className="max-w-xs text-sm text-content-muted">
                Describe the vibe, set a duration, and compose.
              </p>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
